import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatterFileType } from '@models/matter';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessDispatch } from '@state/business';
import {
	reLineDefaults,
	remoteSigningJuratDefault,
	yoursTrulyLawyerDefault,
	downloadedDocumentTitleDefault,
	emailSubjectByTypeDefault,
	inPersonSigningJuratDefault,
} from '@state/business/business.models';

export type ReLineType =
	| 'client'
	| 'condensed'
	| 'debts'
	| 'encumbrances'
	| 'general'
	| 'downloadedDocumentTitle'
	| 'remoteSigningJurat'
	| 'inPersonSigningJurat'
	| 'yoursTrulyLawyer'
	| 'emailSubject'
	| 'mortgages';

@Component({
	selector: 'app-editing-defaults-dialog',
	templateUrl: './editing-defaults-dialog.component.html',
	standalone: false,
})
export class EditingDefaultsDialogComponent {
	public height: string;
	public tag: string;
	public tagLabel: string;
	public type: ReLineType;
	public showHtmlEditor: boolean;

	constructor(
		private businessDispatch: BusinessDispatch,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			matterType: MatterFileType;
			type: ReLineType;
			tag: string;
			tagLabel: string;
		},
		private dialogRef: MatDialogRef<EditingDefaultsDialogComponent>,
		private analytics: AnalyticsService,
	) {
		this.type = data.type;
		this.tag = data.tag;
		this.tagLabel = data.tagLabel;
		this.height = this.type === 'remoteSigningJurat' ? '15rem' : '8rem';
		this.showHtmlEditor = !(
			this.type === 'emailSubject' || this.type === 'downloadedDocumentTitle'
		);
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	onChange($event: string): void {
		this.tag = $event;
	}

	reset(): void {
		switch (this.type) {
			case 'client':
				this.tag = reLineDefaults.clientsByType[this.data.matterType];
				break;
			case 'condensed':
				this.tag = reLineDefaults.condensedByType[this.data.matterType];
				break;
			case 'debts':
				this.tag = reLineDefaults.debts;
				break;
			case 'encumbrances':
				this.tag = reLineDefaults.encumbrances;
				break;
			case 'mortgages':
				this.tag = reLineDefaults.mortgages;
				break;
			case 'general':
				this.tag = reLineDefaults.generalByType[this.data.matterType];
				break;
			case 'downloadedDocumentTitle':
				this.tag = downloadedDocumentTitleDefault;
				break;
			case 'remoteSigningJurat':
				this.tag = remoteSigningJuratDefault;
				break;
			case 'inPersonSigningJurat':
				this.tag = inPersonSigningJuratDefault;
				break;
			case 'yoursTrulyLawyer':
				this.tag = yoursTrulyLawyerDefault;
				break;
			case 'emailSubject':
				this.tag = emailSubjectByTypeDefault[this.data.matterType];
				break;
		}

		if (this.showHtmlEditor)
			this.analytics.logEvent(
				AnalyticsEvent.CONDENSED_EDITOR_BUSINESS_DEFAULTS_RESET_CLICK,
			);
	}

	save(): void {
		switch (this.type) {
			case 'client':
				this.businessDispatch.SetRelineClients(this.tag, this.data.matterType);
				break;
			case 'condensed':
				this.businessDispatch.SetRelineCondensed(
					this.tag,
					this.data.matterType,
				);
				break;
			case 'debts':
				this.businessDispatch.SetRelineDebts(this.tag);
				break;
			case 'encumbrances':
				this.businessDispatch.SetRelineEncumbrances(this.tag);
				break;
			case 'mortgages':
				this.businessDispatch.SetRelineMortgages(this.tag);
				break;
			case 'general':
				this.businessDispatch.SetRelineGeneral(this.tag, this.data.matterType);
				break;
			case 'downloadedDocumentTitle':
				this.businessDispatch.SetDownloadedDocumentTitle(this.tag);
				break;
			case 'remoteSigningJurat':
				this.businessDispatch.SetRemoteSigningJurat(this.tag);
				break;
			case 'inPersonSigningJurat':
				this.businessDispatch.SetInPersonSigningJurat(this.tag);
				break;
			case 'yoursTrulyLawyer':
				this.businessDispatch.SetYoursTrulyLawyer(this.tag);
				break;
			case 'emailSubject':
				switch (this.data.matterType) {
					case MatterFileType.SALE:
						this.businessDispatch.SetDefaultEmailSubjectSale(this.tag);
						break;
					case MatterFileType.PURCHASE:
						this.businessDispatch.SetDefaultEmailSubjectPurchase(this.tag);
						break;
					case MatterFileType.REFINANCE:
						this.businessDispatch.SetDefaultEmailSubjectRefinance(this.tag);
						break;
				}
				break;
		}

		if (this.showHtmlEditor)
			this.analytics.logEvent(
				AnalyticsEvent.CONDENSED_EDITOR_BUSINESS_DEFAULTS_SAVE_CLICK,
			);
		this.closeModal();
	}
}
